import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Users, Zap, ArrowRight, CheckCircle, Code2 } from 'lucide-react';

// Integration logo URLs (official SVGs from Wikimedia / brand CDNs)
const GOOGLE_CALENDAR_LOGO = "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg";
const SHOPIFY_LOGO = "https://customer-assets.emergentagent.com/job_95fa5d1b-93d8-45fa-8fee-36235fc1312c/artifacts/bni9agb4_shopify-bag-icon-symbol-logo-701751695132537nenecmhs0u.png";

const VoiceAgentSection = () => {
  const features = [
    {
      icon: Phone,
      logo: null,
      title: "24/7 Answering",
      desc: "Always available for your customers",
      bg: "bg-purple-500/10",
      color: "text-purple-400",
    },
    {
      icon: null,
      logo: GOOGLE_CALENDAR_LOGO,
      logoAlt: "Google Calendar",
      title: "Book Appointments",
      desc: "Google Calendar integration",
      bg: "bg-white/5",
      color: null,
    },
    {
      icon: null,
      logo: SHOPIFY_LOGO,
      logoAlt: "Shopify",
      title: "Order Support",
      desc: "Shopify order status & tracking",
      bg: "bg-[#96BF48]/10",
      color: null,
    },
    {
      icon: Code2,
      logo: null,
      title: "Custom APIs",
      desc: "Connect any tool or service",
      bg: "bg-blue-500/10",
      color: "text-blue-400",
    },
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-purple-900/10 to-transparent" id="voice-agent">
      <div className="max-w-6xl mx-auto">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2">
            <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm-1 14.5v-5l4.5 2.5-4.5 2.5z"/>
            </svg>
            <span className="text-purple-400 text-sm font-medium">NEW: Ruonn Cloud Platform</span>
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-3xl md:text-5xl font-bold text-white text-center mb-4">
          AI Voice Agent for Your Business
        </h2>
        <p className="text-xl text-gray-400 text-center mb-12 max-w-3xl mx-auto">
          Never miss a call again. Our AI agent answers 24/7, schedules appointments,
          handles orders, and transfers to humans when needed.
        </p>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 text-center hover:border-purple-500/30 transition-colors group"
            >
              {/* Icon or Logo */}
              <div className={`w-14 h-14 ${feature.bg} rounded-xl flex items-center justify-center mx-auto mb-4 transition-transform group-hover:scale-110`}>
                {feature.logo ? (
                  <img
                    src={feature.logo}
                    alt={feature.logoAlt}
                    className="w-9 h-9 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                )}
              </div>
              <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Integration strip */}
        <div className="flex items-center justify-center gap-3 mb-12 flex-wrap">
          <span className="text-gray-500 text-sm">Integrates with</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
              <img src={GOOGLE_CALENDAR_LOGO} alt="Google Calendar" className="w-5 h-5" />
              <span className="text-gray-300 text-xs font-medium">Google Calendar</span>
            </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
              <img src={SHOPIFY_LOGO} alt="Shopify" className="w-5 h-5 object-contain" />
              <span className="text-gray-300 text-xs font-medium">Shopify</span>
            </div>
            <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-full px-4 py-1.5">
              <Code2 className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300 text-xs font-medium">Custom API</span>
            </div>
          </div>
        </div>

        {/* Pricing Preview */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Simple, Transparent Pricing</h3>
              <ul className="space-y-3">
                {[
                  "5 free trial calls - no credit card",
                  "$10/month per business profile",
                  "10 free minutes included",
                  "$0.25/minute after free tier",
                  "$2/month per phone number"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">$10</div>
              <div className="text-gray-400 mb-6">per business / month</div>
              <Link
                to="/cloud/register"
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
              >
                <Zap className="w-5 h-5" />
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/voice-agent"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-medium transition-colors"
          >
            Learn more about AI Voice Agent
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default VoiceAgentSection;
